import { useMutation, useQuery, useQueryClient, type UseQueryResult } from '@tanstack/react-query'
import { apiTokenService } from '@/services/ApiTokenService'
import { apiTokenKeys } from './queryKeys'
import type { ApiTokenDto, ApiTokenListDto, CreatedApiTokenDto } from '@lib/dto/api-token.dto'
import type { CreateApiTokenInput } from '@lib/input/api-token.input'
import type { McpWriteListDto } from '@lib/dto/mcp-write.dto'

/** A person's own API tokens. Only ever their own — there is no route to another's. */
export function useApiTokens(enabled = true): UseQueryResult<ApiTokenListDto> {
  return useQuery({
    queryKey: apiTokenKeys.list(),
    queryFn: () => apiTokenService.list(),
    enabled,
  })
}

/**
 * The secret comes back here and nowhere else, so the caller has to show it now.
 * It is not put in the cache: a credential sitting in query state until the tab
 * is closed is a credential with a longer life than it needs.
 */
export function useCreateApiToken() {
  const qc = useQueryClient()
  return useMutation<CreatedApiTokenDto, Error, CreateApiTokenInput>({
    mutationFn: (input) => apiTokenService.create(input),
    onSuccess: () => void qc.invalidateQueries({ queryKey: apiTokenKeys.all }),
  })
}

/** What one token changed through MCP tools, fetched when it is asked for. */
export function useApiTokenChanges(id: string | null): UseQueryResult<McpWriteListDto> {
  return useQuery({
    queryKey: apiTokenKeys.changes(id ?? ''),
    queryFn: () => apiTokenService.changes(id ?? ''),
    enabled: id !== null,
  })
}

export function useRevokeApiToken() {
  const qc = useQueryClient()
  return useMutation<ApiTokenDto, Error, string>({
    mutationFn: (id) => apiTokenService.revoke(id),
    onSuccess: () => void qc.invalidateQueries({ queryKey: apiTokenKeys.all }),
  })
}
